// 🎯 DashboardUserSyncStatus.tsx - CLEAN VERSION - Built from scratch
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import { Table, Input, message, Button } from 'antd';
import { SearchOutlined, ReloadOutlined, MobileOutlined } from '@ant-design/icons';
import { Users, Smartphone, CheckCircle, XCircle, Clock, WifiOff } from 'lucide-react';
import moment from 'moment';
import debounce from 'lodash/debounce';
import { useSelector } from 'react-redux';
import type { ColumnsType } from 'antd/es/table';

// Import the existing API service
import { getUserOrganizationList } from '../../services/dashboardService';
import logger from '../../utils/logger';

// Console logging - as per user preferences
logger.log('📱 DashboardUserSyncStatus - CLEAN VERSION - Starting fresh');

// 🎯 SIMPLE INTERFACES - No complex statistics
interface DeviceInfo {
  deviceName: string;
  model: string;
  readableVersion: string;
}

interface UserSyncData {
  _id: string;
  firstName: string;
  lastName?: string;
  phone: string;
  deviceInfo: DeviceInfo[];
  lastCallLogSyncAt?: { $date: string } | null;
  lastCallRecordingSyncAt?: { $date: string } | null;
  lastCallRecordApiTime?: { $date: string } | null;
  lastContactSyncAt?: { $date: string } | null;
}

// 🎯 SIMPLE COMPONENT PROPS
interface DashboardUserSyncStatusProps {
  fromDate: string;
  toDate: string;
  selectedUserId?: string;
  selectedTeamId?: string;
  isOrganization?: boolean;
  title?: string;
}

const DashboardUserSyncStatus: React.FC<DashboardUserSyncStatusProps> = ({
  fromDate,
  toDate,
  selectedUserId,
  selectedTeamId,
  isOrganization,
  title = "Team Member Sync Status"
}) => {
  logger.log('🔄 Clean component rendered with props:', {
    fromDate, toDate, selectedUserId, selectedTeamId, isOrganization
  });

  // 🎯 SIMPLE STATE MANAGEMENT - No statistics
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState<UserSyncData[]>([]);
  const [filteredData, setFilteredData] = useState<UserSyncData[]>([]);
  const [searchText, setSearchText] = useState('');

  // 🎯 REDUX ACCESS - Role checking
  const StoreData = useSelector((state: any) => state?.rootReducers);
  const userRole = StoreData?.userData?.userDetails?.role?.name;

  logger.log('🔐 User role check:', userRole);

  // 🎯 SIMPLE DEVICE INFO PROCESSING
  const getDeviceInfo = useCallback((deviceInfo: DeviceInfo[]) => {
    logger.log('📱 Processing device info:', deviceInfo);
    
    if (!deviceInfo || deviceInfo.length === 0) {
      return {
        display: 'No Device',
        hasDevice: false,
        version: 'N/A',
        icon: <WifiOff size={16} color="#dc2626" />
      };
    }

    // Find device with highest version
    let highestVersionDevice = deviceInfo[0];
    let highestVersion = "0.0.0.0";

    deviceInfo.forEach(device => {
      if (device.readableVersion && compareVersions(device.readableVersion, highestVersion) > 0) {
        highestVersion = device.readableVersion;
        highestVersionDevice = device;
      }
    });

    const deviceName = highestVersionDevice.deviceName || highestVersionDevice.model || "Unknown Device";
    const version = highestVersionDevice.readableVersion || "Unknown";
    
    // Simple device icon
    let deviceIcon = <Smartphone size={16} color="#3b82f6" />;
    if (deviceName.toLowerCase().includes('iphone')) {
      deviceIcon = <MobileOutlined style={{ color: '#3b82f6', fontSize: '16px' }} />;
    }
    
    return {
      display: deviceName,
      hasDevice: true,
      version: version,
      icon: deviceIcon
    };
  }, []);

  // 🎯 SIMPLE VERSION COMPARISON
  const compareVersions = useCallback((version1: string, version2: string): number => {
    const v1Parts = version1.split('.').map(Number);
    const v2Parts = version2.split('.').map(Number);
    
    for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
      const v1Part = v1Parts[i] || 0;
      const v2Part = v2Parts[i] || 0;
      
      if (v1Part > v2Part) return 1;
      if (v1Part < v2Part) return -1;
    }
    return 0;
  }, []);

  // 🎯 SIMPLE TIMESTAMP FORMATTING
  const formatTimestamp = useCallback((timestamp: { $date: string } | null | undefined) => {
    logger.log('⏰ Formatting timestamp:', timestamp);
    
    if (!timestamp || !timestamp.$date) {
      return {
        display: 'Never Synced',
        icon: <XCircle size={14} color="#dc2626" />,
        color: '#dc2626',
        bgColor: '#fef2f2'
      };
    }
    
    const date = new Date(timestamp.$date);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    let timeAgo: string;
    let icon: React.ReactNode;
    let color: string;
    let bgColor: string;
    
    if (diffMins < 1) {
      timeAgo = "Just now";
      icon = <CheckCircle size={14} color="#059669" />;
      color = '#059669';
      bgColor = '#f0fdf4';
    } else if (diffMins < 60) {
      timeAgo = `${diffMins}m ago`;
      icon = <CheckCircle size={14} color={diffMins > 30 ? "#d97706" : "#059669"} />;
      color = diffMins > 30 ? '#d97706' : '#059669';
      bgColor = diffMins > 30 ? '#fffbeb' : '#f0fdf4';
    } else if (diffHours < 24) {
      timeAgo = `${diffHours}h ago`;
      icon = <Clock size={14} color={diffHours > 12 ? "#d97706" : "#059669"} />;
      color = diffHours > 12 ? '#d97706' : '#059669';
      bgColor = diffHours > 12 ? '#fffbeb' : '#f0fdf4';
    } else {
      timeAgo = `${diffDays}d ago`;
      icon = <XCircle size={14} color="#dc2626" />;
      color = '#dc2626';
      bgColor = '#fef2f2';
    }
    
    const fullDateTime = date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    return {
      display: timeAgo,
      icon: icon,
      color: color,
      bgColor: bgColor,
      fullDateTime: fullDateTime
    };
  }, []);

  // 🎯 SIMPLE DATA FETCHING
  const fetchUserSyncData = useCallback(async () => {
    logger.log('🔄 Fetching user sync data - clean version');
    
    try {
      setLoading(true);
      
      const params = {
        page: 1,
        perPage: 100,
        ...(selectedTeamId && { teamId: selectedTeamId }),
        ...(selectedUserId && { userId: selectedUserId })
      };
      
      logger.log('📡 API call with params:', params);
      const response = await getUserOrganizationList(params);
      
      logger.log('✅ API response:', response);
      
      if (response && response.status && response.data && response.data.data) {
        const users = response.data.data as UserSyncData[];
        logger.log('👥 Users received:', users.length);
        
        setUserData(users);
        setFilteredData(users);
        
        logger.log('✅ User sync data processed successfully');
      } else {
        logger.log('⚠️ Invalid response format');
        message.warning('Invalid response format from server');
        setUserData([]);
        setFilteredData([]);
      }
      
    } catch (error) {
      console.error('❌ Error fetching user sync data:', error);
      message.error('Failed to fetch user sync data');
      setUserData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTeamId, selectedUserId]);

  // 🎯 SIMPLE SEARCH FUNCTIONALITY
  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      logger.log('🔍 Search triggered:', searchTerm);
      
      if (!searchTerm.trim()) {
        setFilteredData(userData);
        return;
      }

      const term = searchTerm.toLowerCase();
      const filtered = userData.filter(user => {
        const fullName = `${user.firstName} ${user.lastName || ''}`.toLowerCase();
        const phone = user.phone.toString();
        return fullName.includes(term) || phone.includes(term);
      });
      
      setFilteredData(filtered);
      logger.log('🔍 Search results:', filtered.length);
    }, 500),
    [userData]
  );

  // 🎯 SIMPLE SEARCH AND REFRESH HANDLERS
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedSearch(value);
  };

  const handleRefresh = () => {
    logger.log('🔄 Manual refresh triggered');
    setSearchText('');
    debouncedSearch.cancel();
    fetchUserSyncData();
  };

  // 🎯 SIMPLE STATUS BADGE COMPONENT
  const StatusBadge: React.FC<{ syncData: any }> = ({ syncData }) => {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 10px',
        borderRadius: '6px',
        backgroundColor: syncData.bgColor,
        border: `1px solid ${syncData.color}20`,
        minWidth: '120px',
        fontSize: '12px',
        fontWeight: '500'
      }}>
        {syncData.icon}
        <span style={{ color: syncData.color }}>
          {syncData.display}
        </span>
      </div>
    );
  };

  // 🎯 SIMPLE TABLE COLUMNS
  const columns: ColumnsType<UserSyncData> = [
    {
      title: 'Team Member',
      key: 'user',
      width: 200,
      fixed: 'left',
      render: (_, record) => {
        const fullName = `${record.firstName} ${record.lastName || ''}`.trim();
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              fontWeight: 'bold',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
            }}>
              {record.firstName.charAt(0).toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ 
                fontWeight: 600, 
                fontSize: '14px',
                color: '#1f2937',
                marginBottom: '2px'
              }}>
                {fullName}
              </div>
              <div style={{ 
                fontSize: '12px', 
                color: '#6b7280',
                fontFamily: 'monospace',
                background: '#f3f4f6',
                padding: '2px 6px',
                borderRadius: '4px',
                display: 'inline-block'
              }}>
                {record.phone}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Device & Version',
      key: 'device',
      width: 200,
      render: (_, record) => {
        const deviceInfo = getDeviceInfo(record.deviceInfo);
        
        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            borderRadius: '8px',
            backgroundColor: deviceInfo.hasDevice ? '#f0f9ff' : '#fef2f2',
            border: `1px solid ${deviceInfo.hasDevice ? '#bae6fd' : '#fecaca'}`
          }}>
            {deviceInfo.icon}
            <div>
              <div style={{ 
                fontWeight: 600, 
                color: '#374151',
                fontSize: '13px',
                marginBottom: '2px'
              }}>
                {deviceInfo.display}
              </div>
              {deviceInfo.hasDevice && (
                <div style={{ 
                  color: '#6b7280',
                  fontSize: '11px',
                  background: '#ffffff',
                  padding: '1px 4px',
                  borderRadius: '3px',
                  display: 'inline-block'
                }}>
                  v{deviceInfo.version}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Call Log Sync',
      key: 'callLog',
      width: 140,
      render: (_, record) => {
        const sync = formatTimestamp(record.lastCallLogSyncAt);
        return <StatusBadge syncData={sync} />;
      },
    },
    {
      title: 'Recording Sync',
      key: 'recording',
      width: 140,
      render: (_, record) => {
        const sync = formatTimestamp(record.lastCallRecordingSyncAt);
        return <StatusBadge syncData={sync} />;
      },
    },
    {
      title: 'API Status',
      key: 'api',
      width: 140,
      render: (_, record) => {
        const sync = formatTimestamp(record.lastCallRecordApiTime);
        return <StatusBadge syncData={sync} />;
      },
    },
    
  ];

  // 🎯 LOAD DATA ON MOUNT
  useEffect(() => {
    if (userRole === "super_admin") {
      logger.log('🔄 Loading user sync data');
      fetchUserSyncData();
    }
  }, [fetchUserSyncData, userRole]);

  // 🎯 ROLE-BASED ACCESS CONTROL
  if (userRole !== "super_admin") {
    logger.log('🚫 Access denied: User is not super_admin');
    return null;
  }

  return (
    <Card style={{ 
      borderRadius: '16px', 
      border: '1px solid #e2e8f0', 
      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
      marginTop: '24px',
      overflow: 'hidden'
    }}>
      {/* CLEAN SIMPLE HEADER - NO STATISTICS */}
      <CardHeader style={{ 
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', 
        borderBottom: '1px solid #e2e8f0',
        padding: '20px 24px'
      }}>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '16px',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}>
              <Users size={24} color="white" />
            </div>
            <div>
              <h4 className="mb-1 fw-bold text-dark" style={{ fontSize: '20px' }}>
                {title}
              </h4>
              <p className="mb-0" style={{ fontSize: '13px', color: '#6b7280' }}>
                Super Admin Access • Real-time Sync Monitoring
              </p>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <Input
              placeholder="🔍 Search by name or phone..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={handleSearch}
              allowClear
              style={{ 
                width: 300,
                borderRadius: '8px',
                border: '1px solid #d1d5db'
              }}
              disabled={loading}
            />
            
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
              type="default"
              style={{
                borderRadius: '8px',
                height: '32px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      
      {/* CLEAN SIMPLE TABLE - NO STATISTICS */}
      <CardBody style={{ padding: '0' }}>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="_id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `Showing ${range[0]}-${range[1]} of ${total} team members`,
            position: ['bottomCenter'],
            style: { padding: '16px 24px' }
          }}
          scroll={{ x: 1200, y: 500 }}
          size="middle"
          style={{ background: 'white' }}
          locale={{
            emptyText: searchText ? 'No team members found for your search' : 'No sync data available'
          }}
          rowClassName={(record) => {
            const hasDevice = record.deviceInfo && record.deviceInfo.length > 0;
            const hasSyncIssues = !record.lastCallLogSyncAt || !record.lastCallRecordingSyncAt || 
                                 !record.lastContactSyncAt;
            
            if (!hasDevice || hasSyncIssues) {
              return 'sync-issue-row';
            }
            return 'sync-healthy-row';
          }}
        />
      </CardBody>

      {/* Simple Custom Styles */}
      <style>{`
        .sync-healthy-row {
          background-color: #f9fafb !important;
          transition: all 0.2s ease;
        }
        .sync-healthy-row:hover {
          background-color: #f0f9ff !important;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .sync-issue-row {
          background-color: #fef2f2 !important;
          border-left: 3px solid #ef4444;
          transition: all 0.2s ease;
        }
        .sync-issue-row:hover {
          background-color: #fee2e2 !important;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(239,68,68,0.15);
        }
        .ant-table-thead > tr > th {
          background: #f8fafc !important;
          border-bottom: 2px solid #e2e8f0 !important;
          font-weight: 600 !important;
          color: #374151 !important;
          padding: 16px 12px !important;
        }
        .ant-table-tbody > tr > td {
          padding: 12px !important;
          border-bottom: 1px solid #f1f5f9 !important;
        }
      `}</style>
    </Card>
  );
};

export default DashboardUserSyncStatus;